import React from 'react';
import { connect } from 'react-redux';
import * as R from 'ramda';
import { Link } from 'react-router-dom';
import ScrollArea from 'react-scrollbar';
import moment from 'moment';

import IconLeft from 'material-ui/svg-icons/navigation/chevron-left';
import IconRight from 'material-ui/svg-icons/navigation/chevron-right';

import * as companyActions from '../../actions/entities/CompanyActions';
import { Grid, GridHeader, GridBody, GridFooter, GridRow, GridCell } from '../../components/basic/grid';
import IconButton from '../../components/basic/IconButton';
import Button from '../../components/basic/Button';
import PlainTextField from '../../components/basic/PlainTextField';

import { getCompaniesFilteredPage } from '../../selectors/CompaniesSelector';
import { CONTRACT_PLAN_TYPES } from '../../constants/Constants';
import CompanyItemProjectsList from './CompanyItemProjectsList';

const creditNumberText = c => {
  const plan = c.contractPlan || {};
  const profileLimit = plan.profileLimit || {};
  if (!profileLimit.number) {
    return 'Unlimited number of credits';
  }
  const creditCounter = (c.profileCounters && c.profileCounters.global) || { count: 0 };
  const countStyle = creditCounter.count >= profileLimit.number ? { color: 'red' } : {};
  return (
    <span style={countStyle}>
      <strong>{creditCounter.count}</strong> of <strong>{profileLimit.number}</strong> credits used
    </span>
  );
};

const creditExpiryText = c => {
  const plan = c.contractPlan || {};
  const { expiration } = plan.profileLimit || {};
  if (!expiration) {
    return 'Credit expiry not set';
  }
  const expired = expiration < Date.now();
  const expiryStyle = expired ? { color: 'red' } : {};
  return (
    <span style={expiryStyle}>
      Credits expire{expired ? 'd' : ''} <strong>{moment(expiration).format('L')}</strong>
    </span>
  );
};

const contractPlanTypeText = c => {
  const plan = c.contractPlan || {};
  const { type } = plan;
  if (!type) {
    return 'Contract plan type not set';
  }
  return (
    <span>
      <strong>{(R.find(c => c.value === type, CONTRACT_PLAN_TYPES) || { label: type }).label}</strong> contract plan
    </span>
  );
};

const infoText = c => {
  return (
    <span>
      {contractPlanTypeText(c)}&nbsp; &nbsp;|&nbsp; &nbsp;{creditNumberText(c)}&nbsp; | &nbsp;{creditExpiryText(c)}
    </span>
  );
};

const MODES = [
  {
    key: 'SOURCING',
    label: 'Sourcing'
  },
  {
    key: 'RECRUITMENT',
    label: 'Recruitment'
  },
  {
    key: 'DEVELOPMENT',
    label: 'Development'
  }
];

const modesInfoText = c => {
  const hasMode = (plan, mode) => plan && plan.modes && plan.modes.indexOf(mode) > -1;
  return (
    <span>
      {R.addIndex(R.map)(
        ({ key, label }, idx) => (
          <span key={key}>
            {idx > 0 && <span>&nbsp;&nbsp;|&nbsp;&nbsp;</span>}
            <span style={{ fontWeight: hasMode(c.contractPlan, key) ? 500 : 300 }}>{label}</span>
          </span>
        ),
        MODES
      )}
    </span>
  );
};

const internalTagsText = c => {
  const tags = (c.data && c.data.internalTags) || [];
  return (
    <span>
      {R.map(
        tag => (
          <span key={tag} className="badge badge-info" style={{ marginRight: '6px' }}>
            {tag}
          </span>
        ),
        tags
      )}
    </span>
  );
};

class CompaniesContainer extends React.Component {
  componentDidMount() {
    const { fetchCompanies } = this.props;
    fetchCompanies();
  }

  render() {
    const { companies, filter, setCompaniesFilter, setCompaniesPage } = this.props;
    return (
      <div className="flex flex1">
        <Grid>
          <GridHeader>
            <GridRow className="row">
              <GridCell className="col-sm-6">
                <span>Company</span>
              </GridCell>
              <GridCell className="col-sm-4">
                <span>Slug</span>
              </GridCell>
              <GridCell className="col-sm-2">
                <span>Created at</span>
              </GridCell>
            </GridRow>
            <GridRow className="row" style={{ padding: 0 }}>
              <GridCell className="col-sm-12" style={{ padding: 0 }}>
                <PlainTextField
                  placeholder="Filter..."
                  value={filter}
                  onChange={e => setCompaniesFilter(e.target.value)}
                  label="Filter"
                />
              </GridCell>
            </GridRow>
          </GridHeader>
          <ScrollArea smoothScrolling>
            <GridBody>
              {R.map(
                c => (
                  <Link key={c.id} to={`/settings/companies/${c.id}`}>
                    <GridRow className="row">
                      <GridCell className="col-sm-6">
                        <span>{c.name}</span>
                        <br />
                        <small className="font-weight-light">{infoText(c)}</small>
                        <br />
                        <small className="font-weight-light">{modesInfoText(c)}</small>
                        <br />
                        <small className="font-weight-light">{internalTagsText(c)}</small>
                      </GridCell>
                      <GridCell className="col-sm-4">
                        <span>{c.slug}</span>
                      </GridCell>
                      <GridCell className="col-sm-2">
                        <span>{moment(c.createdAt).format('L')}</span>
                        <br />
                        <span>{moment(c.createdAt).format('LT')}</span>
                      </GridCell>
                      <GridCell className="col-sm-12" style={{ borderTop: '1px dashed #e0e0e0', marginTop: '0.5em' }}>
                        <div style={{ padding: '0.25em 0.25em 0 0.25em' }}>
                          <CompanyItemProjectsList company={c} />
                        </div>
                      </GridCell>
                    </GridRow>
                  </Link>
                ),
                companies.items
              )}
            </GridBody>
          </ScrollArea>
          <GridFooter>
            <GridRow className="Grid-pagination row">
              <GridCell className="col-sm-12">
                <div className="clearfix">
                  <div className="float-left" style={{ padding: '0.5em' }}>
                    <Button
                      label="Create"
                      raised
                      primary
                      style={{ fontSize: '16px' }}
                      containerElement={<Link to="/settings/companies/new" />}
                    />
                  </div>
                  <div className="float-right">
                    <span>
                      {companies.page * companies.size + 1}-{(companies.page + 1) * companies.size} of{' '}
                      {companies.totalSize}
                    </span>
                    <IconButton onClick={() => setCompaniesPage(companies.page - 1)} disabled={companies.page <= 0}>
                      <IconLeft />
                    </IconButton>
                    <IconButton
                      onClick={() => setCompaniesPage(companies.page + 1)}
                      disabled={(companies.page + 1) * companies.size > companies.totalSize}
                    >
                      <IconRight />
                    </IconButton>
                  </div>
                </div>
              </GridCell>
            </GridRow>
          </GridFooter>
        </Grid>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  companies: getCompaniesFilteredPage(state),
  filter: state.entities.companies.filter
});

export default connect(mapStateToProps, companyActions)(CompaniesContainer);



// WEBPACK FOOTER //
// ./src/containers/private/CompaniesContainer.js